import React, {useMemo, useState} from 'react';
import {Box, CircularProgress, Typography} from "@mui/joy";
import {Button} from "@mui/material";
import {useMutation, useQuery} from "@apollo/client";
import {useNavigate} from "react-router-dom";
import {useKeycloak} from "@react-keycloak/web";
import {useTranslation} from "react-i18next";
import _ from "lodash";
import {toast} from "react-toastify";
import {AddRounded} from "@mui-symbols-material/w300";
import {Error, getUserResources, hasAppAccess} from "@dreebit-registry/vsm-microservice-components";
import {InstalledBaseNodeContactPersonList} from "../../graphql/queries/installedBaseNodeContactPersonList";
import {InstalledBaseNodeServiceUnit} from "../../graphql/queries/installedBaseNodeServiceUnit";
import {RemoveContactPersonFromInstalledBaseNode} from "../../graphql/mutations/removeContactPersonFromInstalledBaseNode";
import {resolveErrorMessage, sanitizeErrorForDisplay} from "../../utils/resolveErrorMessage";
import {buildInstalledBasePathname} from "../../utils/installedBaseUrl";
import {getPathNodesAbove} from "../../utils/installedBasePath";
import useOwnClient from "../../hooks/useOwnClient";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import ServiceUnitInfoPanel from "../ServiceUnitPanel/ServiceUnitInfoPanel";
import ContactPersonCard from "./ContactPersonCard";
import ContactPersonFormModal from "./ContactPersonFormModal";
import AddContactModal from "./AddContactModal";
import {ContactAssignment, ContactPersonInfo, partitionAssignments} from "../../utils/contactAssignments";

// No pager and no filters in the tab — a node carries few contacts. Above the
// limit a caption says so.
const LIST_LIMIT = 100;

interface Props {
    nodeId: string;
    // the node owner's client id — assigning/creating needs it (a node only
    // accepts its own client's contacts); without it the tab is read-only
    ownerId?: string;
    canWrite: boolean;
}

/**
 * Contact tab body of the node detail panel: the responsible service
 * center/department resolved through node → client → region → global
 * (IBM-972) as a widget on top, then the node's contact persons split into
 * directly assigned and inherited cards.
 *
 * Perspective only changes wording: a customer reads "your contact persons"
 * at the unit; a user of the service organisation (internal company) sees
 * the unit's named contacts.
 */
const InstalledBaseNodeContacts = ({nodeId, ownerId, canWrite}: Props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {keycloak} = useKeycloak();
    const ownClient = useOwnClient();
    const isProviderView = !!ownClient?.isServiceProvider;

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [personToEdit, setPersonToEdit] = useState<ContactPersonInfo | null>(null);
    const [personToRemove, setPersonToRemove] = useState<ContactPersonInfo | null>(null);
    const [isRemoving, setIsRemoving] = useState(false);

    const listQuery = useQuery(InstalledBaseNodeContactPersonList, {
        variables: {
            nodeId,
            filter: {includeInherited: true},
            pagination: {start: 0, limit: LIST_LIMIT},
        },
        fetchPolicy: "cache-and-network",
    });

    // Resolved service unit of this node (null = no read access or no global
    // fallback configured — the panel is simply omitted then).
    const serviceUnitQuery = useQuery(InstalledBaseNodeServiceUnit, {
        variables: {nodeId},
        fetchPolicy: "cache-and-network",
    });
    const serviceUnit = _.get(serviceUnitQuery, 'data.installedBaseNode.serviceUnit', null);

    // "Change" leads to the client's service page in the CRM (the client-level
    // assignment lives there) — offered to users who may assign service units
    // and can open the CRM at all. Cross-app link: same origin in production,
    // the testing deployment otherwise (useAppLinks convention).
    const canChangeServiceUnit = !!ownerId
        && hasAppAccess(keycloak.token, 'crm')
        && getUserResources(keycloak.token, 'otto_crm').includes('assignServiceUnits');
    const crmBaseUrl = process.env.NODE_ENV !== 'production'
        ? 'https://testing.buschgroup.io'
        : window.location.origin;
    const serviceUnitChangeHref = canChangeServiceUnit ? `${crmBaseUrl}/crm/customers/${ownerId}/service` : undefined;

    const assignments: ContactAssignment[] =
        (_.get(listQuery, 'data.installedBaseNode.contactPersonList.assignments', []) ?? []).filter(Boolean);
    const total = _.get(listQuery, 'data.installedBaseNode.contactPersonList.total', 0);
    const {direct, inherited} = useMemo(() => partitionAssignments(assignments), [assignments]);
    const directPersonIds = useMemo(() => direct.map((a) => a.contactPerson.id), [direct]);

    const isInitialLoading = listQuery.loading && !listQuery.data;

    const [removeMutation] = useMutation(RemoveContactPersonFromInstalledBaseNode);

    const handleRemoveConfirm = async () => {
        if (!personToRemove) return;
        setIsRemoving(true);
        try {
            const result = await removeMutation({
                variables: {contactPersonId: personToRemove.id, installedBaseNodeId: nodeId},
                refetchQueries: ['InstalledBaseNodeContactPersonList'],
            });
            if (result.data?.removeContactPersonFromInstalledBaseNode?.success) {
                toast.success(t('Contact "{{name}}" removed from this element.', {name: personToRemove.name}));
            } else {
                toast.error(result.data?.removeContactPersonFromInstalledBaseNode?.message || t('Failed to remove contact.'));
            }
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        } finally {
            setIsRemoving(false);
            setPersonToRemove(null);
        }
    };

    // The panel is a child of the tree — pushing the ancestor path as the URL is
    // enough, the tree's URL sync effect re-opens the columns along it and mounts
    // the panel on the ancestor with the contact tab active.
    const navigateToOrigin = (inheritedFrom: NonNullable<ContactAssignment['inheritedFrom']>) => {
        const ancestorIds = getPathNodesAbove(inheritedFrom)
            .map((pathNode) => pathNode.id)
            .filter((id): id is string => !!id);
        const selected = [null, ...ancestorIds, inheritedFrom.id];
        navigate({
            pathname: buildInstalledBasePathname(selected),
            search: '?detail=contact',
        });
    };

    const renderSection = (title: string, sectionAssignments: ContactAssignment[], testId: string) => (
        <Box data-testid={testId}>
            <Typography level="bodyBold" sx={{color: '#666666', mb: '10px'}}>
                {title} ({sectionAssignments.length})
            </Typography>
            {/* CSS grid instead of Joy Grid: its negative spacing margins make the
                tab's scroll container overflow horizontally by a few pixels */}
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))'}, gap: '12px'}}>
                {sectionAssignments.map((assignment) => (
                    <ContactPersonCard
                        key={assignment.id}
                        assignment={assignment}
                        canWrite={canWrite}
                        onEdit={setPersonToEdit}
                        onRemove={setPersonToRemove}
                        onNavigateToOrigin={navigateToOrigin}
                    />
                ))}
            </Box>
        </Box>
    );

    const servicePanel = serviceUnit?.unit ? (
        <Box data-testid="contact-section-service">
            <ServiceUnitInfoPanel
                resolved={serviceUnit}
                perspective={isProviderView ? 'provider' : 'customer'}
                onNavigateToOrigin={navigateToOrigin}
                changeHref={serviceUnitChangeHref}
            />
        </Box>
    ) : null;

    const addButton = canWrite && !!ownerId ? (
        <Button
            variant="contained"
            size="small"
            startIcon={<AddRounded fontSize="small"/>}
            onClick={() => setAddModalOpen(true)}
            data-testid="contact-add"
        >
            {t('Add contact')}
        </Button>
    ) : null;

    return (
        <Box
            data-testid="node-detail-contact"
            sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                px: '20px',
                pt: '12px',
                pb: '12px',
                gap: '12px',
            }}
        >
            {isInitialLoading ? (
                <Box sx={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <CircularProgress variant="soft"/>
                </Box>
            ) : listQuery.error && !listQuery.data ? (
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Error errorObject={sanitizeErrorForDisplay(listQuery.error, t)}/>
                </Box>
            ) : (
                <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '32px'}}>
                    {servicePanel}

                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'}}>
                            <Typography level="bodyBold" sx={{color: '#333'}}>
                                {t('Contact persons')} ({total})
                            </Typography>
                            {addButton}
                        </Box>

                        {assignments.length === 0 ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', p: 3}}>
                                <Typography level="body-sm" sx={{color: 'text.secondary', textAlign: 'center'}}>
                                    {t('No contacts have been assigned to this element yet.')}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {direct.length > 0 && renderSection(t('Directly assigned'), direct, 'contact-section-direct')}
                                {inherited.length > 0 && renderSection(t('Inherited'), inherited, 'contact-section-inherited')}
                                {total > LIST_LIMIT && (
                                    <Typography level="caption" sx={{color: '#666666', textAlign: 'center'}}>
                                        {t('Showing the first {{count}} contacts.', {count: LIST_LIMIT})}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            )}

            {addModalOpen && !!ownerId && (
                <AddContactModal
                    open={addModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    nodeId={nodeId}
                    ownerId={ownerId}
                    assignedPersonIds={directPersonIds}
                />
            )}

            {personToEdit && (
                <ContactPersonFormModal
                    mode="update"
                    contactPersonId={personToEdit.id}
                    sourceKind={personToEdit.sourceKind}
                    open={!!personToEdit}
                    onClose={() => setPersonToEdit(null)}
                    onSuccess={(resultData) => {
                        const name = _.get(resultData, 'updateContactPerson.name');
                        toast.success(t('Contact "{{name}}" updated.', {name}));
                        setPersonToEdit(null);
                    }}
                    refetchQueries={['InstalledBaseNodeContactPersonList']}
                />
            )}

            <ConfirmDeleteModal
                open={!!personToRemove}
                title={t('Remove contact')}
                message={t('Remove "{{name}}" from this element? The contact itself is kept and stays available on other elements.', {name: personToRemove?.name || ''})}
                confirmLabel={t('Remove')}
                loading={isRemoving}
                onConfirm={handleRemoveConfirm}
                onClose={() => setPersonToRemove(null)}
            />
        </Box>
    );
};

export default InstalledBaseNodeContacts;
